import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import EntityDrawer, { SecondaryPanel } from '../ui/EntityDrawer';
import Tabs from '../ui/Tabs';
import CustomerTypeahead, {
  CustomerTypeaheadOption,
} from '../customer/CustomerTypeahead';
import AccountTypeahead, {
  AccountTypeaheadOption,
} from '../account/AccountTypeahead';
import ReservationTypeahead, {
  ReservationTypeaheadOption,
} from '../reservations/ReservationTypeahead';
import RecordContactSnapshotsPanel from '../contacts/RecordContactSnapshotsPanel';
import { adminAPI, apiClient, paymentRequestAPI } from '../../services/api';
import { openResolvedExternalUrl } from '../../utils/externalWindow';
import {
  GroupedPaymentRequestAdminSummary,
  GroupedPaymentRequestAdminAllocation,
} from '../../types/paymentRequest';
import { Invoice } from '../../types/invoice';
import {
  formatCurrencyCents,
  formatDateShort,
  formatLineItemCount,
  groupedRequestStatusLabel,
  statusLabel,
} from '../../utils/invoiceFormatting';

type GroupedFeeActivity = {
  stripe_fee: number;
  platform_fee: number;
  total_fees: number;
  net_amount: number;
  amount_paid: number;
};

type DrawerTab = 'overview' | 'invoices' | 'activity';
type SecondaryView =
  | 'customer'
  | 'account'
  | 'reservation'
  | 'allocation'
  | null;

type Props = {
  open: boolean;
  requestId: string | null;
  onClose: () => void;
  onOpenInvoice?: (invoiceId: string) => void;
  onRequestUpdated?: () => void | Promise<void>;
};

function getStripeBaseUrl(): string {
  const hostname = window.location.hostname.toLowerCase();
  const isSandboxEnvironment =
    process.env.NODE_ENV !== 'production' ||
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1') ||
    hostname.includes('dev') ||
    hostname.includes('staging') ||
    hostname.includes('sandbox');

  return isSandboxEnvironment
    ? 'https://dashboard.stripe.com/test'
    : 'https://dashboard.stripe.com';
}

function getStripePaymentUrl(
  objectId: string | null | undefined,
  connectedAccountId?: string | null
) {
  if (!objectId) return null;

  const baseUrl = `${getStripeBaseUrl()}/payments/${objectId}`;
  if (!connectedAccountId) return baseUrl;

  return `${baseUrl}?account=${encodeURIComponent(connectedAccountId)}`;
}

function getStripeInvoiceUrl(
  invoiceId: string | null | undefined,
  connectedAccountId?: string | null
) {
  if (!invoiceId) return null;

  const baseUrl = `${getStripeBaseUrl()}/invoices/${invoiceId}`;
  if (!connectedAccountId) return baseUrl;

  return `${baseUrl}?account=${encodeURIComponent(connectedAccountId)}`;
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(
      values.filter((value): value is string =>
        Boolean(value && String(value).trim())
      )
    )
  );
}

function titleFromStatus(status?: string | null): string {
  if (!status) return 'Unknown';
  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

export default function GroupedPaymentRequestDrawer({
  open,
  requestId,
  onClose,
  onOpenInvoice,
  onRequestUpdated,
}: Props) {
  const navigate = useNavigate();

  const [drawerTab, setDrawerTab] = useState<DrawerTab>('overview');
  const [secondaryView, setSecondaryView] = useState<SecondaryView>(null);
  const [selectedAllocation, setSelectedAllocation] =
    useState<GroupedPaymentRequestAdminAllocation | null>(null);

  const [drawerGroupedLink, setDrawerGroupedLink] = useState<string | null>(
    null
  );
  const [isLoadingGroupedPaymentLink, setIsLoadingGroupedPaymentLink] =
    useState(false);
  const [groupedPaymentLinkError, setGroupedPaymentLinkError] = useState<
    string | null
  >(null);
  const [isSendingGroupedPaymentLink, setIsSendingGroupedPaymentLink] =
    useState(false);
  const [invalidatingGroupedRequestId, setInvalidatingGroupedRequestId] =
    useState<string | null>(null);
  const [manualPaidPanelOpen, setManualPaidPanelOpen] = useState(false);
  const [manualPaidMethod, setManualPaidMethod] = useState('check');
  const [manualPaidNote, setManualPaidNote] = useState('');
  const [manualPaidSubmitting, setManualPaidSubmitting] = useState(false);
  const [reconcilingGroupedRequest, setReconcilingGroupedRequest] =
    useState(false);

  const [associationSaving, setAssociationSaving] = useState(false);
  const [selectedCustomerOption, setSelectedCustomerOption] =
    useState<CustomerTypeaheadOption | null>(null);
  const [selectedAccountOption, setSelectedAccountOption] =
    useState<AccountTypeaheadOption | null>(null);
  const [selectedReservationOption, setSelectedReservationOption] =
    useState<ReservationTypeaheadOption | null>(null);
  const [manualReservationRef, setManualReservationRef] = useState('');

  useEffect(() => {
    if (requestId) {
      setDrawerTab('overview');
      setSecondaryView(null);
      setSelectedAllocation(null);
      setDrawerGroupedLink(null);
      setIsLoadingGroupedPaymentLink(false);
      setGroupedPaymentLinkError(null);
      setSelectedCustomerOption(null);
      setSelectedAccountOption(null);
      setSelectedReservationOption(null);
      setManualReservationRef('');
      setManualPaidPanelOpen(false);
      setManualPaidMethod('check');
      setManualPaidNote('');
      setManualPaidSubmitting(false);
      setReconcilingGroupedRequest(false);
    }
  }, [requestId]);

  const {
    data: request,
    isLoading: requestLoading,
    refetch: refetchRequest,
  } = useQuery<GroupedPaymentRequestAdminSummary | null>(
    ['grouped-payment-request-drawer', requestId],
    async () => {
      if (!requestId) return null;
      const response = await paymentRequestAPI.get(requestId);
      return response.data;
    },
    {
      enabled: open && Boolean(requestId),
      staleTime: 30_000,
    }
  );

  const { data: groupedFeeActivity } = useQuery<GroupedFeeActivity | null>(
    ['grouped-payment-request-drawer-fee-activity', request?.id],
    async () => {
      if (!request?.id) return null;
      const response = await paymentRequestAPI.getFeeActivity(request.id);
      return response.data;
    },
    {
      enabled: open && Boolean(request?.id) && request?.status === 'paid',
      staleTime: 60_000,
    }
  );

  const { data: childInvoices, refetch: refetchChildInvoices } = useQuery<
    Invoice[]
  >(
    ['grouped-payment-request-child-invoices', request?.id],
    async () => {
      if (!request) return [];
      const invoiceIds = uniqueStrings(
        request.invoice_allocations.map((item) => item.invoice_id)
      );
      if (invoiceIds.length === 0) return [];

      const responses = await Promise.allSettled(
        invoiceIds.map(async (invoiceId) => {
          const response = await adminAPI.getInvoice(invoiceId);
          const payload = response?.data as any;
          const invoice = (payload?.invoice || payload) as Invoice;
          if (!invoice?.id) {
            throw new Error(
              `Invoice ${invoiceId} did not return a valid payload`
            );
          }
          return invoice;
        })
      );

      return responses
        .filter(
          (item): item is PromiseFulfilledResult<Invoice> =>
            item.status === 'fulfilled'
        )
        .map((item) => item.value);
    },
    {
      enabled: open && Boolean(request?.id),
      staleTime: 30_000,
    }
  );

  const requestStatusLabel = request
    ? statusLabel(groupedRequestStatusLabel(request.status))
    : 'N/A';

  const childInvoiceById = useMemo(() => {
    const entries = (childInvoices || []).map(
      (invoice) => [invoice.id, invoice] as const
    );
    return new Map<string, Invoice>(entries);
  }, [childInvoices]);

  const rolledUp = useMemo(() => {
    const invoices = childInvoices || [];

    const customerIds = uniqueStrings([
      request?.customer_record_id || request?.customer_id || null,
      ...(request?.invoice_allocations || []).map(
        (allocation) => allocation.customer_record_id || null
      ),
      ...invoices.map((invoice) => invoice.customer_id || null),
    ]);
    const accountIds = uniqueStrings(
      invoices.map((invoice) => invoice.account_id || null)
    );
    const reservationIds = uniqueStrings(
      invoices.map((invoice) => invoice.reservation_id || null)
    );

    const customerId =
      request?.customer_record_id ||
      request?.customer_id ||
      (customerIds.length === 1 ? customerIds[0] : null);
    const accountId = accountIds.length === 1 ? accountIds[0] : null;
    const reservationId =
      reservationIds.length === 1 ? reservationIds[0] : null;

    const customerDetails = customerId
      ? invoices.find((invoice) => invoice.customer_id === customerId)
          ?.customer_details || null
      : null;

    return {
      customerId,
      accountId,
      reservationId,
      customerMixed: customerIds.length > 1,
      accountMixed: accountIds.length > 1,
      reservationMixed: reservationIds.length > 1,
      customerDetails,
    };
  }, [
    childInvoices,
    request?.customer_id,
    request?.customer_record_id,
    request?.invoice_allocations,
  ]);

  useEffect(() => {
    if (rolledUp.reservationId) {
      setManualReservationRef(rolledUp.reservationId);
    }
  }, [rolledUp.reservationId]);

  const { data: linkedCustomer } = useQuery(
    ['grouped-request-linked-customer', rolledUp.customerId],
    async () => {
      const response = await adminAPI.getCustomer(rolledUp.customerId!);
      return response.data as any | null;
    },
    {
      enabled: open && Boolean(rolledUp.customerId),
      staleTime: 120_000,
      retry: false,
    }
  );

  const { data: linkedAccount } = useQuery(
    ['grouped-request-linked-account', rolledUp.accountId],
    async () => {
      const response = await adminAPI.getAccount(rolledUp.accountId!);
      return response.data as { id: string; name: string } | null;
    },
    {
      enabled: open && Boolean(rolledUp.accountId),
      staleTime: 120_000,
      retry: false,
    }
  );

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const reservationIsUuid = Boolean(
    rolledUp.reservationId && uuidRegex.test(rolledUp.reservationId)
  );

  const { data: linkedReservation } = useQuery(
    ['grouped-request-linked-reservation', rolledUp.reservationId],
    async () => {
      const response = await adminAPI.getReservation(rolledUp.reservationId!);
      return response.data as any | null;
    },
    {
      enabled: open && reservationIsUuid,
      staleTime: 120_000,
      retry: false,
    }
  );

  const linkedCustomerRecord =
    linkedCustomer?.customer || linkedCustomer || null;
  const linkedCustomerName = linkedCustomerRecord
    ? [linkedCustomerRecord.first_name, linkedCustomerRecord.last_name]
        .filter(Boolean)
        .join(' ')
        .trim()
    : '';

  const reservationRef =
    linkedReservation?.reservationRef ||
    linkedReservation?.reservation_ref ||
    rolledUp.reservationId ||
    null;

  const stripeReferences = useMemo(() => {
    if (!request) return [];

    return [
      {
        label: 'PaymentIntent',
        value: request.stripe_payment_intent_id,
        href: getStripePaymentUrl(
          request.stripe_payment_intent_id,
          request.stripe_connected_account_id
        ),
      },
      {
        label: 'Stripe invoice',
        value: request.stripe_invoice_id,
        href: getStripeInvoiceUrl(
          request.stripe_invoice_id,
          request.stripe_connected_account_id
        ),
      },
      {
        label: 'Charge',
        value: request.stripe_charge_id,
        href: getStripePaymentUrl(
          request.stripe_charge_id,
          request.stripe_connected_account_id
        ),
      },
    ];
  }, [request]);

  const activityTimeline = useMemo(() => {
    if (!request) return [];

    return [
      {
        label: 'Request created',
        value: request.created_at,
        detail: `Grouped request ${request.id.slice(0, 8)} created`,
      },
      request.paid_at
        ? {
            label: 'Marked paid',
            value: request.paid_at,
            detail:
              request.metadata?.manual_payment === 'true'
                ? `Manual payment recorded${
                    request.metadata?.manual_payment_method
                      ? ` by ${request.metadata.manual_payment_method}`
                      : ''
                  }`
                : 'Stripe marked this grouped request as paid',
          }
        : null,
      request.canceled_at
        ? {
            label: 'Canceled',
            value: request.canceled_at,
            detail: 'The request was canceled and invoice locks were released',
          }
        : null,
      request.replaced_at
        ? {
            label: 'Replaced',
            value: request.replaced_at,
            detail: 'This request was superseded by a newer one',
          }
        : null,
    ].filter(Boolean) as Array<{
      label: string;
      value: string | null;
      detail: string;
    }>;
  }, [request]);

  const notifyUpdated = useCallback(async () => {
    if (!onRequestUpdated) {
      return;
    }
    await onRequestUpdated();
  }, [onRequestUpdated]);

  async function saveAssociationsForInvoices(
    payload: {
      customer_id?: string | null;
      account_id?: string | null;
      reservation_id?: string | null;
    },
    successMessage = 'Associations updated'
  ) {
    if (!request?.invoice_allocations?.length) {
      toast.error('No invoices are available to update');
      return;
    }

    const invoiceIds = uniqueStrings(
      request.invoice_allocations.map((allocation) => allocation.invoice_id)
    );
    if (invoiceIds.length === 0) {
      toast.error('No child invoices found');
      return;
    }

    setAssociationSaving(true);
    try {
      const results = await Promise.allSettled(
        invoiceIds.map((invoiceId) =>
          apiClient.put(`/invoices/admin/${invoiceId}/associations`, payload)
        )
      );

      const successCount = results.filter(
        (result) => result.status === 'fulfilled'
      ).length;
      const failed = results.length - successCount;

      if (successCount === 0) {
        const firstRejected = results.find(
          (result) => result.status === 'rejected'
        ) as PromiseRejectedResult | undefined;
        throw firstRejected?.reason;
      }

      if (failed > 0) {
        toast.error(
          `Updated ${successCount}/${results.length} invoices. ${failed} failed.`
        );
      } else {
        toast.success(successMessage);
      }

      await Promise.all([
        refetchChildInvoices(),
        refetchRequest(),
        notifyUpdated(),
      ]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to update associations'
      );
    } finally {
      setAssociationSaving(false);
    }
  }

  const ensureDrawerGroupedPaymentLink = useCallback(
    async (options: { notify?: boolean } = {}) => {
      if (!request?.id) {
        throw new Error('Grouped request is unavailable');
      }

      if (drawerGroupedLink) {
        return drawerGroupedLink;
      }

      const response = await paymentRequestAPI.generateLink(request.id);

      const nextUrl = response.data?.public_url;
      if (!nextUrl) {
        throw new Error('Missing grouped payment link in response');
      }

      setDrawerGroupedLink(nextUrl);
      setGroupedPaymentLinkError(null);

      if (options.notify !== false) {
        await refetchRequest();
        await notifyUpdated();
      }

      return nextUrl;
    },
    [drawerGroupedLink, notifyUpdated, refetchRequest, request?.id]
  );

  useEffect(() => {
    if (
      !open ||
      !request?.id ||
      request.status !== 'pending_payment' ||
      drawerGroupedLink ||
      isLoadingGroupedPaymentLink ||
      groupedPaymentLinkError
    ) {
      return;
    }

    let canceled = false;

    setIsLoadingGroupedPaymentLink(true);
    setGroupedPaymentLinkError(null);

    ensureDrawerGroupedPaymentLink({ notify: false })
      .catch((error: any) => {
        if (!canceled) {
          setGroupedPaymentLinkError(
            error?.response?.data?.message ||
              error?.message ||
              'Unable to load payment link'
          );
        }
      })
      .finally(() => {
        if (!canceled) {
          setIsLoadingGroupedPaymentLink(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [
    open,
    request?.id,
    request?.status,
    drawerGroupedLink,
    isLoadingGroupedPaymentLink,
    groupedPaymentLinkError,
    ensureDrawerGroupedPaymentLink,
  ]);

  const handleCopyGroupedPaymentLink = async () => {
    try {
      const link = await ensureDrawerGroupedPaymentLink();
      await navigator.clipboard.writeText(link);
      toast.success(request?.status === 'paid' ? 'Grouped receipt link copied' : 'Grouped payment link copied');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to copy grouped payment link'
      );
    }
  };

  const handleOpenGroupedPaymentLink = async () => {
    try {
      await openResolvedExternalUrl(() => ensureDrawerGroupedPaymentLink());
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to open grouped payment page'
      );
    }
  };

  const handleSendGroupedPaymentLink = async () => {
    if (!request?.id) {
      toast.error('Grouped request is unavailable');
      return;
    }

    setIsSendingGroupedPaymentLink(true);
    try {
      const response = await paymentRequestAPI.sendLink(request.id);

      const nextUrl = response.data?.public_url;
      if (!nextUrl) {
        toast.error('Missing grouped payment link in response');
        return;
      }

      setDrawerGroupedLink(nextUrl);
      await refetchRequest();
      await notifyUpdated();

      if (response.data?.sent === false) {
        const deliveryError =
          response.data?.email_delivery?.error ||
          'Email delivery is not configured';
        try {
          await navigator.clipboard.writeText(nextUrl);
          toast.error(`${deliveryError}. Link copied instead.`);
        } catch {
          toast.error(`${deliveryError}. Use Copy link to send it manually.`);
        }
        return;
      }

      toast.success('Grouped payment link sent to customer');
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to send grouped payment link'
      );
    } finally {
      setIsSendingGroupedPaymentLink(false);
    }
  };

  const handleInvalidateGroupedPaymentRequest = async () => {
    if (!request?.id) {
      toast.error('Grouped request is unavailable');
      return;
    }

    const confirmInvalidate = window.confirm(
      'Invalidate this grouped payment request? This revokes the public link and unlocks its invoices.'
    );

    if (!confirmInvalidate) {
      return;
    }

    setInvalidatingGroupedRequestId(request.id);
    try {
      await paymentRequestAPI.cancel(request.id, {
        reason: 'Canceled by admin from grouped payment drawer',
      });

      setDrawerGroupedLink(null);
      await refetchRequest();
      await notifyUpdated();
      toast.success('Grouped payment request invalidated');
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          'Failed to invalidate grouped payment request'
      );
    } finally {
      setInvalidatingGroupedRequestId(null);
    }
  };

  const handleMarkGroupedPaymentPaid = async () => {
    if (!request?.id) {
      toast.error('Grouped request is unavailable');
      return;
    }

    const paymentMethod = manualPaidMethod.trim();
    if (!paymentMethod) {
      toast.error('Payment method is required');
      return;
    }

    setManualPaidSubmitting(true);
    try {
      await paymentRequestAPI.markPaid(request.id, {
        paymentMethod,
        note: manualPaidNote.trim() || undefined,
      });

      setManualPaidPanelOpen(false);
      setManualPaidNote('');
      await Promise.all([
        refetchRequest(),
        refetchChildInvoices(),
        notifyUpdated(),
      ]);
      toast.success('Grouped request marked paid');
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to mark grouped request paid'
      );
    } finally {
      setManualPaidSubmitting(false);
    }
  };

  const handleReconcileGroupedPaymentRequest = async () => {
    if (!request?.id) {
      toast.error('Grouped request is unavailable');
      return;
    }

    setReconcilingGroupedRequest(true);
    try {
      const response = await paymentRequestAPI.reconcile(request.id);
      const warnings = response.data?.warnings || [];
      await Promise.all([
        refetchRequest(),
        refetchChildInvoices(),
        notifyUpdated(),
      ]);

      if (warnings.length > 0) {
        toast.error(warnings[0]);
        return;
      }

      const action = response.data?.action || 'reconciled';
      toast.success(action === 'reported_open'
        ? 'Stripe status checked; no repair needed'
        : 'Grouped request reconciled');
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to reconcile grouped request'
      );
    } finally {
      setReconcilingGroupedRequest(false);
    }
  };

  const secondary: SecondaryPanel | undefined = (() => {
    if (!secondaryView || !request) return undefined;

    if (secondaryView === 'customer') {
      return {
        open: true,
        onClose: () => setSecondaryView(null),
        title: linkedCustomerName || request.customer_email || 'Customer',
        subtitle: rolledUp.customerMixed
          ? 'Mixed across grouped invoices'
          : rolledUp.customerId
          ? `Linked · ${rolledUp.customerId.slice(0, 8)}...`
          : 'No customer linked',
        children: (
          <div className="space-y-4">
            <div className="card p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Name</dt>
                  <dd className="text-right font-medium text-on-surface">
                    {rolledUp.customerDetails?.name ||
                      linkedCustomerName ||
                      '-'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Email</dt>
                  <dd className="break-all text-right text-on-surface">
                    {rolledUp.customerDetails?.email ||
                      request.customer_email ||
                      '-'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Customer ID</dt>
                  <dd className="font-mono text-xs text-on-surface-variant">
                    {rolledUp.customerId || '-'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
              <p className="text-sm font-medium text-on-surface">
                Change customer association on all grouped invoices
              </p>
              <CustomerTypeahead
                value={selectedCustomerOption}
                onChange={setSelectedCustomerOption}
                placeholder="Search customer by name or email..."
                disabled={associationSaving}
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!selectedCustomerOption || associationSaving}
                  onClick={() => {
                    if (selectedCustomerOption) {
                      void saveAssociationsForInvoices(
                        { customer_id: selectedCustomerOption.id },
                        'Customer association updated for grouped invoices'
                      );
                    }
                  }}
                >
                  Link selected
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={associationSaving || !rolledUp.customerId}
                  onClick={() =>
                    void saveAssociationsForInvoices(
                      { customer_id: null },
                      'Customer association cleared for grouped invoices'
                    )
                  }
                >
                  Clear
                </button>
              </div>
            </div>

            {rolledUp.customerId && (
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={() => {
                  onClose();
                  navigate(
                    `/admin/customers?search=${encodeURIComponent(
                      request.customer_email || ''
                    )}`
                  );
                }}
              >
                Open customer page &gt;
              </button>
            )}
          </div>
        ),
      };
    }

    if (secondaryView === 'account') {
      return {
        open: true,
        onClose: () => setSecondaryView(null),
        title:
          linkedAccount?.name ||
          (rolledUp.accountMixed ? 'Multiple accounts' : 'Account'),
        subtitle: rolledUp.accountMixed
          ? 'Mixed across grouped invoices'
          : rolledUp.accountId
          ? `Linked · ${rolledUp.accountId.slice(0, 8)}...`
          : 'No account linked',
        children: (
          <div className="space-y-4">
            <div className="card p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Name</dt>
                  <dd className="font-medium text-on-surface">
                    {linkedAccount?.name || '-'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Account ID</dt>
                  <dd className="font-mono text-xs text-on-surface-variant">
                    {rolledUp.accountId || '-'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
              <p className="text-sm font-medium text-on-surface">
                Change account association on all grouped invoices
              </p>
              <AccountTypeahead
                value={selectedAccountOption}
                onChange={setSelectedAccountOption}
                placeholder="Search account by name..."
                disabled={associationSaving}
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!selectedAccountOption || associationSaving}
                  onClick={() => {
                    if (selectedAccountOption) {
                      void saveAssociationsForInvoices(
                        { account_id: selectedAccountOption.id },
                        'Account association updated for grouped invoices'
                      );
                    }
                  }}
                >
                  Link selected
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={associationSaving || !rolledUp.accountId}
                  onClick={() =>
                    void saveAssociationsForInvoices(
                      { account_id: null },
                      'Account association cleared for grouped invoices'
                    )
                  }
                >
                  Clear
                </button>
              </div>
            </div>

            {rolledUp.accountId && (
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={() => {
                  onClose();
                  navigate(`/admin/accounts/${rolledUp.accountId}`);
                }}
              >
                Open full account page &gt;
              </button>
            )}
          </div>
        ),
      };
    }

    if (secondaryView === 'reservation') {
      return {
        open: true,
        onClose: () => setSecondaryView(null),
        title:
          reservationRef ||
          (rolledUp.reservationMixed ? 'Multiple reservations' : 'Reservation'),
        subtitle: rolledUp.reservationMixed
          ? 'Mixed across grouped invoices'
          : rolledUp.reservationId
          ? `Linked · ${String(reservationRef || rolledUp.reservationId).slice(
              0,
              16
            )}...`
          : 'No reservation linked',
        children: (
          <div className="space-y-4">
            <div className="card p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Reference</dt>
                  <dd className="text-right font-medium text-on-surface">
                    {reservationRef || '-'}
                  </dd>
                </div>
                {linkedReservation?.status && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Status</dt>
                    <dd className="capitalize text-on-surface">
                      {linkedReservation.status}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Reservation ID</dt>
                  <dd className="font-mono text-xs text-on-surface-variant">
                    {rolledUp.reservationId || '-'}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-xs text-on-surface-variant">
                Linking here updates all invoices in this grouped request.
              </p>
            </div>

            <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
              <p className="text-sm font-medium text-on-surface">
                Change reservation association on all grouped invoices
              </p>
              <ReservationTypeahead
                value={selectedReservationOption}
                onChange={setSelectedReservationOption}
                placeholder="Search reservations by ref or customer..."
                disabled={associationSaving}
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!selectedReservationOption || associationSaving}
                  onClick={() => {
                    if (selectedReservationOption) {
                      void saveAssociationsForInvoices(
                        { reservation_id: selectedReservationOption.id },
                        'Reservation association updated for grouped invoices'
                      );
                    }
                  }}
                >
                  Link selected
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={associationSaving || !rolledUp.reservationId}
                  onClick={() =>
                    void saveAssociationsForInvoices(
                      { reservation_id: null },
                      'Reservation association cleared for grouped invoices'
                    )
                  }
                >
                  Clear
                </button>
              </div>

              <div className="space-y-2 border-t border-ghost pt-2">
                <p className="text-xs font-medium text-on-surface-variant">
                  Or set a manual reservation reference
                </p>
                <input
                  type="text"
                  className="input-field w-full text-sm"
                  placeholder="Example: RES-2026-001"
                  value={manualReservationRef}
                  onChange={(event) =>
                    setManualReservationRef(event.target.value)
                  }
                />
                <button
                  type="button"
                  className="btn-secondary w-full"
                  disabled={associationSaving}
                  onClick={() =>
                    void saveAssociationsForInvoices(
                      { reservation_id: manualReservationRef.trim() || null },
                      'Reservation reference updated for grouped invoices'
                    )
                  }
                >
                  Save reference
                </button>
              </div>
            </div>

            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                onClose();
                navigate('/admin/reservations');
              }}
            >
              Open reservations page &gt;
            </button>
          </div>
        ),
      };
    }

    if (secondaryView === 'allocation' && selectedAllocation) {
      const allocation = selectedAllocation;
      const childInvoice = childInvoiceById.get(allocation.invoice_id);

      return {
        open: true,
        onClose: () => {
          setSecondaryView(null);
          setSelectedAllocation(null);
        },
        title: allocation.snapshot_invoice_number || 'Invoice allocation',
        subtitle: `${titleFromStatus(
          allocation.status
        )} · ${formatCurrencyCents(
          allocation.allocation_amount_cents,
          request.currency
        )}`,
        children: (
          <div className="space-y-4">
            <div className="card p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Invoice</dt>
                  <dd className="font-medium text-on-surface">
                    {allocation.snapshot_invoice_number || '-'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Allocated amount</dt>
                  <dd className="font-semibold text-on-surface">
                    {formatCurrencyCents(
                      allocation.allocation_amount_cents,
                      request.currency
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">
                    Snapshot amount due
                  </dt>
                  <dd className="text-on-surface">
                    {formatCurrencyCents(
                      allocation.snapshot_amount_due_cents,
                      request.currency
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Lock status</dt>
                  <dd className="capitalize text-on-surface">
                    {allocation.lock_status.replace(/_/g, ' ')}
                  </dd>
                </div>
                {allocation.due_date && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">
                      Snapshot due date
                    </dt>
                    <dd className="text-on-surface">
                      {formatDateShort(allocation.due_date)}
                    </dd>
                  </div>
                )}
              </dl>
              {(allocation.description || allocation.memo) && (
                <p className="mt-3 text-xs text-on-surface-variant">
                  {allocation.description || allocation.memo}
                </p>
              )}
            </div>

            {childInvoice && (
              <div className="card p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                  Live invoice details
                </p>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Status</dt>
                    <dd className="capitalize text-on-surface">
                      {childInvoice.status}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Total</dt>
                    <dd className="text-on-surface">
                      {formatCurrencyCents(
                        childInvoice.total,
                        childInvoice.currency
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Paid</dt>
                    <dd className="text-on-surface">
                      {formatCurrencyCents(
                        childInvoice.amount_paid,
                        childInvoice.currency
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Customer</dt>
                    <dd className="text-right text-on-surface">
                      {childInvoice.customer_details?.name ||
                        childInvoice.customer_details?.email ||
                        '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Account ID</dt>
                    <dd className="font-mono text-xs text-on-surface-variant">
                      {childInvoice.account_id || '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Reservation ID</dt>
                    <dd className="font-mono text-xs text-on-surface-variant">
                      {childInvoice.reservation_id || '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-on-surface-variant">Due</dt>
                    <dd className="text-on-surface">
                      {childInvoice.due_date
                        ? formatDateShort(childInvoice.due_date)
                        : '-'}
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            {childInvoice ? (
              <RecordContactSnapshotsPanel
                snapshots={childInvoice.contactSnapshots}
                title="Child Invoice Contact Resolution"
                subtitle="Snapshot contacts used by this invoice allocation."
              />
            ) : null}

            {(allocation.source_stripe_invoice_id_snapshot ||
              childInvoice?.payment_intent_id) && (
              <div className="card p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                  Stripe references
                </p>
                <dl className="space-y-3 text-xs">
                  {allocation.source_stripe_invoice_id_snapshot && (
                    <div>
                      <dt className="text-on-surface-variant">
                        Stripe invoice snapshot
                      </dt>
                      <dd className="mt-0.5 break-all font-mono text-on-surface">
                        {allocation.source_stripe_invoice_id_snapshot}
                      </dd>
                    </div>
                  )}
                  {childInvoice?.payment_intent_id && (
                    <div>
                      <dt className="text-on-surface-variant">
                        Payment intent
                      </dt>
                      <dd className="mt-0.5 break-all font-mono text-on-surface">
                        {childInvoice.payment_intent_id}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                if (onOpenInvoice) {
                  onOpenInvoice(allocation.invoice_id);
                } else {
                  onClose();
                  navigate(`/admin/invoices?invoiceId=${encodeURIComponent(allocation.invoice_id)}`);
                }
              }}
            >
              Open invoice
            </button>
          </div>
        ),
      };
    }

    return undefined;
  })();

  return (
    <EntityDrawer
      open={open}
      title={
        request
          ? `Grouped request ${request.id.slice(0, 8)}`
          : 'Grouped request'
      }
      subtitle={request?.customer_email || 'Unknown customer'}
      onClose={onClose}
      secondary={secondary}
    >
      {!requestId ? null : requestLoading && !request ? (
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Loading grouped request details...
          </p>
        </div>
      ) : !request ? (
        <p className="text-sm text-on-surface-variant">
          Grouped request not found.
        </p>
      ) : (
        <div className="space-y-6">
          {/* ── Persistent action bar ── */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSendGroupedPaymentLink}
              disabled={
                request.status !== 'pending_payment' ||
                isSendingGroupedPaymentLink
              }
              className="btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {isSendingGroupedPaymentLink ? 'Sending...' : 'Email link'}
            </button>
            <button
              type="button"
              onClick={handleCopyGroupedPaymentLink}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              {request.status === 'paid' ? 'Copy receipt link' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={handleOpenGroupedPaymentLink}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              {request.status === 'paid' ? 'Open receipt' : 'Open link'}
            </button>
            <button
              type="button"
              onClick={() => setManualPaidPanelOpen((current) => !current)}
              disabled={
                request.status !== 'pending_payment' || manualPaidSubmitting
              }
              className="btn-secondary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <BanknotesIcon className="h-4 w-4" />
              Mark paid
            </button>
            <button
              type="button"
              onClick={handleReconcileGroupedPaymentRequest}
              disabled={reconcilingGroupedRequest}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${reconcilingGroupedRequest ? 'animate-spin' : ''}`} />
              {reconcilingGroupedRequest ? 'Checking...' : 'Reconcile'}
            </button>
            <button
              type="button"
              onClick={handleInvalidateGroupedPaymentRequest}
              disabled={
                request.status !== 'pending_payment' ||
                invalidatingGroupedRequestId === request.id
              }
              className="inline-flex items-center rounded-pill px-5 py-2 text-sm font-medium text-white transition-all bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              <NoSymbolIcon className="mr-1.5 h-4 w-4" />
              {invalidatingGroupedRequestId === request.id
                ? 'Invalidating...'
                : 'Invalidate'}
            </button>
          </div>

          <div className="overflow-x-auto">
            <Tabs
              tabs={[
                { key: 'overview', label: 'Overview' },
                {
                  key: 'invoices',
                  label: 'Invoices',
                  count: request.invoice_allocations.length,
                },
                { key: 'activity', label: 'Activity' },
              ]}
              activeTab={drawerTab}
              onChange={(tab) => setDrawerTab(tab as DrawerTab)}
              className="min-w-max"
            />
          </div>

          {drawerTab === 'overview' && (
            <div className="space-y-4">
              <div className="card p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                      Status
                    </p>
                    <p className="mt-1 text-lg font-semibold text-on-surface">
                      {requestStatusLabel}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                      Total
                    </p>
                    <p className="mt-1 text-lg font-bold text-primary">
                      {formatCurrencyCents(
                        request.request_total_cents,
                        request.currency
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-on-surface-variant">
                      {request.invoice_allocations.length} invoice
                      {request.invoice_allocations.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                      Created
                    </p>
                    <p className="mt-1 text-sm text-on-surface">
                      {formatDateShort(request.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                      Expires
                    </p>
                    <p className="mt-1 text-sm text-on-surface">
                      {request.expires_at
                        ? formatDateShort(request.expires_at)
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <RecordContactSnapshotsPanel
                snapshots={request.contactSnapshots}
                subtitle="Recipient snapshot used for the grouped payment request."
              />

              {manualPaidPanelOpen && request.status === 'pending_payment' && (
                <div className="card border border-primary/30 bg-primary/5 p-4">
                  <div className="flex items-start gap-3">
                    <BanknotesIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <div className="min-w-0 flex-1 space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-on-surface">
                          Record manual payment
                        </h4>
                        <p className="mt-1 text-sm text-on-surface-variant">
                          This marks {request.invoice_allocations.length}{' '}
                          invoice
                          {request.invoice_allocations.length !== 1 ? 's' : ''}{' '}
                          paid for{' '}
                          {formatCurrencyCents(
                            request.request_total_cents,
                            request.currency
                          )}{' '}
                          and marks the grouped Stripe invoice paid out of band.
                        </p>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-[minmax(0,180px),1fr]">
                        <label className="block">
                          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                            Method
                          </span>
                          <select
                            className="input-field w-full text-sm"
                            value={manualPaidMethod}
                            onChange={(event) =>
                              setManualPaidMethod(event.target.value)
                            }
                            disabled={manualPaidSubmitting}
                          >
                            <option value="check">Check</option>
                            <option value="cash">Cash</option>
                            <option value="ach">ACH</option>
                            <option value="wire">Wire</option>
                            <option value="other">Other</option>
                          </select>
                        </label>

                        <label className="block">
                          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                            Note
                          </span>
                          <input
                            type="text"
                            className="input-field w-full text-sm"
                            placeholder="Example: Check #1042 received"
                            value={manualPaidNote}
                            onChange={(event) =>
                              setManualPaidNote(event.target.value)
                            }
                            disabled={manualPaidSubmitting}
                          />
                        </label>
                      </div>

                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          className="btn-secondary text-sm"
                          disabled={manualPaidSubmitting}
                          onClick={() => setManualPaidPanelOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn-primary text-sm disabled:opacity-50"
                          disabled={
                            manualPaidSubmitting || !manualPaidMethod.trim()
                          }
                          onClick={handleMarkGroupedPaymentPaid}
                        >
                          {manualPaidSubmitting
                            ? 'Recording payment...'
                            : 'Record payment'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="card p-4">
                <h4 className="mb-3 text-sm font-semibold text-on-surface">
                  Associations
                </h4>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() =>
                      setSecondaryView(
                        secondaryView === 'customer' ? null : 'customer'
                      )
                    }
                    className={`flex w-full items-center justify-between gap-3 rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${
                      secondaryView === 'customer'
                        ? 'border-primary bg-surface-container-low'
                        : 'border-ghost'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <UserCircleIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-on-surface">
                          {rolledUp.customerDetails?.name ||
                            linkedCustomerName ||
                            request.customer_email ||
                            '-'}
                        </p>
                        <p className="text-xs text-on-surface-variant">
                          {rolledUp.customerMixed
                            ? 'Mixed across grouped invoices'
                            : rolledUp.customerId
                            ? 'Linked customer'
                            : 'No customer linked'}
                        </p>
                      </div>
                    </div>
                    <ChevronRightIcon
                      className={`h-4 w-4 flex-shrink-0 transition-transform ${
                        secondaryView === 'customer'
                          ? 'rotate-180 text-primary'
                          : 'text-on-surface-variant'
                      }`}
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setSecondaryView(
                        secondaryView === 'account' ? null : 'account'
                      )
                    }
                    className={`flex w-full items-center justify-between gap-3 rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${
                      secondaryView === 'account'
                        ? 'border-primary bg-surface-container-low'
                        : 'border-ghost'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <BuildingOfficeIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-on-surface">
                          {rolledUp.accountMixed
                            ? 'Multiple accounts'
                            : linkedAccount?.name || '-'}
                        </p>
                        <p className="text-xs text-on-surface-variant">
                          {rolledUp.accountMixed
                            ? 'Mixed across grouped invoices'
                            : rolledUp.accountId
                            ? 'Linked account'
                            : 'No account linked'}
                        </p>
                      </div>
                    </div>
                    <ChevronRightIcon
                      className={`h-4 w-4 flex-shrink-0 transition-transform ${
                        secondaryView === 'account'
                          ? 'rotate-180 text-primary'
                          : 'text-on-surface-variant'
                      }`}
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setSecondaryView(
                        secondaryView === 'reservation' ? null : 'reservation'
                      )
                    }
                    className={`flex w-full items-center justify-between gap-3 rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${
                      secondaryView === 'reservation'
                        ? 'border-primary bg-surface-container-low'
                        : 'border-ghost'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-on-surface">
                          {rolledUp.reservationMixed
                            ? 'Multiple reservations'
                            : reservationRef
                            ? reservationRef.length > 20
                              ? `${reservationRef.slice(0, 8)}...`
                              : reservationRef
                            : '-'}
                        </p>
                        <p className="text-xs text-on-surface-variant">
                          {rolledUp.reservationMixed
                            ? 'Mixed across grouped invoices'
                            : rolledUp.reservationId
                            ? 'Linked reservation'
                            : 'No reservation linked'}
                        </p>
                      </div>
                    </div>
                    <ChevronRightIcon
                      className={`h-4 w-4 flex-shrink-0 transition-transform ${
                        secondaryView === 'reservation'
                          ? 'rotate-180 text-primary'
                          : 'text-on-surface-variant'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {(request.status === 'pending_payment' || drawerGroupedLink) && (
                <div className="card p-4">
                  <h4 className="mb-3 text-sm font-semibold text-on-surface">
                    {request.status === 'paid' ? 'Receipt Link' : 'Payment Link'}
                  </h4>
                  {drawerGroupedLink ? (
                    <div className="flex items-center gap-2">
                      <p className="min-w-0 flex-1 truncate text-sm text-primary">
                        {drawerGroupedLink}
                      </p>
                      <button
                        type="button"
                        className="btn-secondary inline-flex flex-shrink-0 items-center gap-1.5 text-sm"
                        onClick={handleCopyGroupedPaymentLink}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                        Copy
                      </button>
                      <button
                        type="button"
                        className="btn-secondary inline-flex flex-shrink-0 items-center gap-1.5 text-sm"
                        onClick={handleOpenGroupedPaymentLink}
                      >
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                        Open
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-on-surface-variant">
                      {isLoadingGroupedPaymentLink
                        ? 'Loading payment link...'
                        : groupedPaymentLinkError ||
                          'Payment link is not available for this request.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {drawerTab === 'invoices' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                  {request.invoice_allocations.length} invoice
                  {request.invoice_allocations.length !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-on-surface-variant">
                  Total{' '}
                  {formatCurrencyCents(
                    request.request_total_cents,
                    request.currency
                  )}
                </p>
              </div>

              {request.invoice_allocations.map((allocation) => {
                const childInvoice = childInvoiceById.get(
                  allocation.invoice_id
                );
                const isActive =
                  secondaryView === 'allocation' &&
                  selectedAllocation?.id === allocation.id;
                const detailParts = [
                  allocation.description ||
                    allocation.memo ||
                    formatLineItemCount(allocation.line_item_count),
                  childInvoice?.status
                    ? `Status ${titleFromStatus(childInvoice.status)}`
                    : null,
                  childInvoice?.due_date
                    ? `Due ${formatDateShort(childInvoice.due_date)}`
                    : null,
                ].filter(Boolean);

                return (
                  <button
                    key={allocation.id}
                    type="button"
                    onClick={() => {
                      if (isActive) {
                        setSelectedAllocation(null);
                        setSecondaryView(null);
                        return;
                      }
                      setSelectedAllocation(allocation);
                      setSecondaryView('allocation');
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${
                      isActive
                        ? 'border-primary bg-surface-container-low'
                        : 'border-ghost'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-on-surface">
                          {allocation.snapshot_invoice_number}
                        </p>
                        <p className="text-xs text-on-surface-variant">
                          {detailParts.join(' · ')}
                          {allocation.lock_status
                            ? ` · ${allocation.lock_status.replace(/_/g, ' ')}`
                            : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <span className="text-sm font-semibold text-on-surface">
                        {formatCurrencyCents(
                          allocation.allocation_amount_cents,
                          request.currency
                        )}
                      </span>
                      <ChevronRightIcon
                        className={`h-4 w-4 transition-transform ${
                          isActive
                            ? 'rotate-180 text-primary'
                            : 'text-on-surface-variant'
                        }`}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {drawerTab === 'activity' && (
            <div className="space-y-4">
              <div className="card p-4">
                <h4 className="mb-3 text-sm font-semibold text-on-surface">
                  Timeline
                </h4>
                <div className="space-y-3">
                  {activityTimeline.length > 0 ? (
                    activityTimeline.map((event) => (
                      <div
                        key={`${event.label}-${event.detail}`}
                        className="rounded-card border border-ghost bg-surface-container-low p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-on-surface">
                            {event.label}
                          </p>
                          <p className="text-xs text-on-surface-variant">
                            {event.value ? formatDateShort(event.value) : 'N/A'}
                          </p>
                        </div>
                        <p className="mt-1 text-sm text-on-surface-variant">
                          {event.detail}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-on-surface-variant">
                      No activity recorded yet.
                    </p>
                  )}
                </div>
              </div>

              {request.metadata?.manual_payment === 'true' && (
                <div className="card p-4">
                  <h4 className="mb-3 text-sm font-semibold text-on-surface">
                    Manual payment
                  </h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Method</dt>
                      <dd className="capitalize text-on-surface">
                        {request.metadata.manual_payment_method || '-'}
                      </dd>
                    </div>
                    {request.metadata.manual_payment_note && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-on-surface-variant">Note</dt>
                        <dd className="text-right text-on-surface">
                          {request.metadata.manual_payment_note}
                        </dd>
                      </div>
                    )}
                    {request.metadata.manual_payment_stripe_invoice_action && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-on-surface-variant">
                          Stripe invoice
                        </dt>
                        <dd className="text-right text-on-surface">
                          {String(
                            request.metadata
                              .manual_payment_stripe_invoice_action
                          ).replace(/_/g, ' ')}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              <div className="card p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-on-surface">
                    Stripe references
                  </h4>
                  <span className="rounded-pill border border-ghost px-2.5 py-0.5 text-xs text-on-surface-variant">
                    {request.stripe_connected_account_id
                      ? 'Connected account'
                      : 'Platform'}
                  </span>
                </div>
                <div className="space-y-2">
                  {stripeReferences.map((entry) => (
                    <div
                      key={entry.label}
                      className="rounded-card border border-ghost bg-surface-container-low p-3"
                    >
                      <p className="text-xs uppercase tracking-[0.14em] text-on-surface-variant">
                        {entry.label}
                      </p>
                      <div className="mt-1 flex items-center justify-between gap-3">
                        <p className="min-w-0 break-all text-sm font-medium text-on-surface">
                          {entry.value || (
                            <span className="text-on-surface-variant">
                              Not set
                            </span>
                          )}
                        </p>
                        {entry.href && entry.value && (
                          <a
                            href={entry.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 text-xs font-medium text-primary hover:underline"
                          >
                            Open
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {request.status === 'paid' && groupedFeeActivity && (
                <div className="card p-4">
                  <h4 className="mb-3 text-sm font-semibold text-on-surface">
                    Fee breakdown
                  </h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Amount paid</dt>
                      <dd className="font-medium text-on-surface">
                        {formatCurrencyCents(
                          groupedFeeActivity.amount_paid,
                          request.currency
                        )}
                      </dd>
                    </div>
                    {groupedFeeActivity.stripe_fee > 0 && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-on-surface-variant">Stripe fee</dt>
                        <dd className="text-on-surface">
                          -
                          {formatCurrencyCents(
                            groupedFeeActivity.stripe_fee,
                            request.currency
                          )}
                        </dd>
                      </div>
                    )}
                    {groupedFeeActivity.platform_fee > 0 && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-on-surface-variant">
                          Platform fee
                        </dt>
                        <dd className="text-on-surface">
                          -
                          {formatCurrencyCents(
                            groupedFeeActivity.platform_fee,
                            request.currency
                          )}
                        </dd>
                      </div>
                    )}
                    {groupedFeeActivity.total_fees > 0 && (
                      <div className="flex justify-between gap-2 border-t border-ghost pt-2">
                        <dt className="text-on-surface-variant">Total fees</dt>
                        <dd className="text-on-surface">
                          -
                          {formatCurrencyCents(
                            groupedFeeActivity.total_fees,
                            request.currency
                          )}
                        </dd>
                      </div>
                    )}
                    {groupedFeeActivity.net_amount > 0 && (
                      <div className="flex justify-between gap-2">
                        <dt className="font-medium text-on-surface">
                          Net to company
                        </dt>
                        <dd className="font-semibold text-on-surface">
                          {formatCurrencyCents(
                            groupedFeeActivity.net_amount,
                            request.currency
                          )}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </EntityDrawer>
  );
}
